import axios from "axios";
import React, { useEffect, useRef, useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import swal from "sweetalert";
import UseFullPageLoader from "../../component/FullPageLoader/UseFullPageLoader";
import PostAutoComplete from "./PostAutoComplete";

const MEDIA_BASE_URL = (
  process.env.REACT_APP_MEDIA_URL ||
  process.env.REACT_APP_API_URL ||
  ""
).replace(/\/$/, "");

const parseMediaList = (value) => {
  if (!value) {
    return [];
  }

  if (Array.isArray(value)) {
    return value;
  }

  if (typeof value === "object") {
    if (Array.isArray(value.data)) {
      return value.data;
    }

    if (Array.isArray(value.items)) {
      return value.items;
    }

    return [value];
  }

  if (typeof value === "string") {
    const trimmedValue = value.trim();

    if (!trimmedValue) {
      return [];
    }

    try {
      const parsedValue = JSON.parse(trimmedValue);

      if (Array.isArray(parsedValue)) {
        return parsedValue;
      }

      return parsedValue ? [parsedValue] : [];
    } catch (error) {
      return trimmedValue
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean);
    }
  }

  return [];
};

const getMediaPath = (item, type) => {
  if (!item) {
    return "";
  }

  if (typeof item === "string") {
    return item;
  }

  return (
    item.url ||
    item.full_url ||
    item.file_url ||
    item.public_url ||
    item.path ||
    item.file_path ||
    item.storage_path ||
    item[type] ||
    item.file ||
    item.filename ||
    item.file_name ||
    item.name ||
    ""
  );
};

const getMediaName = (item, path, type, index) => {
  if (typeof item === "object" && item !== null) {
    return (
      item.original_name ||
      item.original_filename ||
      item.file_name ||
      item.filename ||
      item.name ||
      path.split("/").pop() ||
      `${type}-${index + 1}`
    );
  }

  return path.split("/").pop() || `${type}-${index + 1}`;
};

const resolveMediaUrl = (path) => {
  if (!path) {
    return "";
  }

  if (
    path.startsWith("http://") ||
    path.startsWith("https://") ||
    path.startsWith("blob:") ||
    path.startsWith("data:")
  ) {
    return path;
  }

  if (path.startsWith("/")) {
    return MEDIA_BASE_URL
      ? `${MEDIA_BASE_URL}${path}`
      : path;
  }

  return MEDIA_BASE_URL
    ? `${MEDIA_BASE_URL}/${path}`
    : `/${path}`;
};

const normalizeMediaList = (value, type) => {
  return parseMediaList(value)
    .map((item, index) => {
      const path = getMediaPath(item, type);

      if (!path) {
        return null;
      }

      return {
        id:
          typeof item === "object" && item !== null
            ? item.id ||
              item.media_id ||
              item.attachment_id ||
              null
            : null,

        path,
        url: resolveMediaUrl(path),
        name: getMediaName(item, path, type, index),
        original: item,
      };
    })
    .filter(Boolean);
};

const detectMediaType = (item) => {
  if (!item) {
    return "";
  }

  const path = getMediaPath(item, "file").toLowerCase();

  const marker =
    typeof item === "object"
      ? String(
          item.type ||
            item.media_type ||
            item.category ||
            item.file_type ||
            item.mime_type ||
            item.mime ||
            ""
        ).toLowerCase()
      : "";

  if (
    marker.includes("image") ||
    /\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i.test(path)
  ) {
    return "image";
  }

  if (
    marker.includes("video") ||
    /\.(mp4|mov|avi|mkv|webm|ogg|m4v)$/i.test(path)
  ) {
    return "video";
  }

  if (
    marker.includes("document") ||
    marker.includes("pdf") ||
    /\.(pdf|doc|docx|xls|xlsx|csv|txt|ppt|pptx)$/i.test(path)
  ) {
    return "document";
  }

  return "";
};

const removeDuplicateMedia = (files) => {
  const uniqueFiles = new Map();

  files.forEach((file) => {
    const key = String(
      file.id || file.path || file.url || file.name
    );

    if (!uniqueFiles.has(key)) {
      uniqueFiles.set(key, file);
    }
  });

  return Array.from(uniqueFiles.values());
};

const extractExistingMedia = (post, type) => {
  if (!post) {
    return [];
  }

  const fieldValues = {
    image: [
      post.images,
      post.image,
      post.post_images,
      post.site_images,
      post.site_post_images,
      post.post_site_images,
      post.image_files,
      post.media?.images,
      post.attachments?.images,
    ],

    video: [
      post.videos,
      post.video,
      post.post_videos,
      post.site_videos,
      post.site_post_videos,
      post.post_site_videos,
      post.video_files,
      post.media?.videos,
      post.attachments?.videos,
    ],

    document: [
      post.documents,
      post.document,
      post.post_documents,
      post.site_documents,
      post.site_post_documents,
      post.post_site_documents,
      post.document_files,
      post.media?.documents,
      post.attachments?.documents,
    ],
  };

  let files = [];

  (fieldValues[type] || []).forEach((value) => {
    files = [
      ...files,
      ...normalizeMediaList(value, type),
    ];
  });

  const genericCollections = [
    post.files,
    post.media_files,
    post.attachments,
    Array.isArray(post.media) ? post.media : null,
  ];

  genericCollections.forEach((collection) => {
    parseMediaList(collection).forEach((item) => {
      if (detectMediaType(item) === type) {
        files = [
          ...files,
          ...normalizeMediaList([item], type),
        ];
      }
    });
  });

  return removeDuplicateMedia(files);
};

const getExistingFileKey = (file) => {
  return String(
    file?.id ||
      file?.path ||
      file?.url ||
      file?.name ||
      ""
  );
};

const EditPostSite = () => {
  document.title = "DirtMap - Edit Post";

  const { id } = useParams();
  const history = useHistory();
  const postFormRef = useRef();

  const [loader, showLoader, hideLoader] =
    UseFullPageLoader();

  const [errorlist, setError] = useState({});
  const [isDisplayed] = useState(true);

  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);

  const [selectedCountryId, setSelectedCountryId] =
    useState("");

  const [selectedStateId, setSelectedStateId] =
    useState("");

  const [selectedCityId, setSelectedCityId] =
    useState("");

  const [isPrefill, setIsPrefill] = useState(true);

  const prefillRef = useRef({
    countryId: "",
    stateId: "",
    cityId: "",
  });

  const prevCountryIdRef = useRef("");
  const prevStateIdRef = useRef("");

  const [postSiteInput, setPostSiteInput] = useState({
    user_id: localStorage.getItem("user_id") || "",
    item_type: "",
    project_name: "",
    cuyds_of_dirt_min: "",
    cuyds_of_dirt_max: "",
    search_location: "",
    address: "",
    latitude: "",
    longitude: "",
    description: "",
    special_instruction: "",
    name: "",
    email: "",
    phone: "",
    company_name: "",
    country: "",
    state: "",
    city: "",
    status: 1,
  });

  /*
   * Newly selected files.
   */
  const [images, setImages] = useState([]);
  const [video, setVideo] = useState([]);
  const [documentFile, setDocumentFile] = useState([]);

  /*
   * Files already saved on the server.
   */
  const [existingImages, setExistingImages] =
    useState([]);

  const [existingVideos, setExistingVideos] =
    useState([]);

  const [existingDocuments, setExistingDocuments] =
    useState([]);

  /*
   * Existing files removed from edit preview.
   */
  const [removedImages, setRemovedImages] =
    useState([]);

  const [removedVideos, setRemovedVideos] =
    useState([]);

  const [removedDocuments, setRemovedDocuments] =
    useState([]);

  const handleInput = (event) => {
    const { name, value } = event.target;

    setPostSiteInput((previousInput) => ({
      ...previousInput,
      [name]: value,
    }));

    setError((previousErrors) => ({
      ...previousErrors,
      [name]: undefined,
    }));
  };

  const handleImage = (event) => {
    const selectedFiles = Array.from(
      event.target.files || []
    );

    const totalImages =
      existingImages.length + selectedFiles.length;

    if (totalImages > 6) {
      setError((previousErrors) => ({
        ...previousErrors,
        image: ["Maximum 6 images are allowed"],
      }));

      event.target.value = "";
      return;
    }

    setImages(selectedFiles);

    setError((previousErrors) => ({
      ...previousErrors,
      image: undefined,
    }));
  };

  const handleVideo = (event) => {
    const selectedFiles = Array.from(
      event.target.files || []
    );

    const totalVideos =
      existingVideos.length + selectedFiles.length;

    if (totalVideos > 6) {
      setError((previousErrors) => ({
        ...previousErrors,
        video: ["Maximum 6 videos are allowed"],
      }));

      event.target.value = "";
      return;
    }

    setVideo(selectedFiles);

    setError((previousErrors) => ({
      ...previousErrors,
      video: undefined,
    }));
  };

  const handleDocument = (event) => {
    const selectedFiles = Array.from(
      event.target.files || []
    );

    const totalDocuments =
      existingDocuments.length +
      selectedFiles.length;

    if (totalDocuments > 6) {
      setError((previousErrors) => ({
        ...previousErrors,
        document: [
          "Maximum 6 documents are allowed",
        ],
      }));

      event.target.value = "";
      return;
    }

    setDocumentFile(selectedFiles);

    setError((previousErrors) => ({
      ...previousErrors,
      document: undefined,
    }));
  };

  const removeImage = (index) => {
    setImages((previousFiles) =>
      previousFiles.filter(
        (_, currentIndex) => currentIndex !== index
      )
    );
  };

  const removeVideo = (index) => {
    setVideo((previousFiles) =>
      previousFiles.filter(
        (_, currentIndex) => currentIndex !== index
      )
    );
  };

  const removeDocument = (index) => {
    setDocumentFile((previousFiles) =>
      previousFiles.filter(
        (_, currentIndex) => currentIndex !== index
      )
    );
  };

  const removeExistingImage = (file) => {
    const fileKey = getExistingFileKey(file);

    setExistingImages((previousFiles) =>
      previousFiles.filter(
        (item) =>
          getExistingFileKey(item) !== fileKey
      )
    );

    setRemovedImages((previousFiles) => {
      if (previousFiles.includes(fileKey)) {
        return previousFiles;
      }

      return [...previousFiles, fileKey];
    });
  };

  const removeExistingVideo = (file) => {
    const fileKey = getExistingFileKey(file);

    setExistingVideos((previousFiles) =>
      previousFiles.filter(
        (item) =>
          getExistingFileKey(item) !== fileKey
      )
    );

    setRemovedVideos((previousFiles) => {
      if (previousFiles.includes(fileKey)) {
        return previousFiles;
      }

      return [...previousFiles, fileKey];
    });
  };

  const removeExistingDocument = (file) => {
    const fileKey = getExistingFileKey(file);

    setExistingDocuments((previousFiles) =>
      previousFiles.filter(
        (item) =>
          getExistingFileKey(item) !== fileKey
      )
    );

    setRemovedDocuments((previousFiles) => {
      if (previousFiles.includes(fileKey)) {
        return previousFiles;
      }

      return [...previousFiles, fileKey];
    });
  };

  /*
   * Supports both callback formats:
   *
   * setPostCoodinationValue(address, lat, lng)
   * setPostCoodinationValue(address, { lat, lng })
   */
  const setPostCoodinationValue = (
    address,
    latitudeOrLocation,
    longitudeValue
  ) => {
    const latitude =
      typeof latitudeOrLocation === "object"
        ? latitudeOrLocation?.lat
        : latitudeOrLocation;

    const longitude =
      typeof latitudeOrLocation === "object"
        ? latitudeOrLocation?.lng
        : longitudeValue;

    setPostSiteInput((previousInput) => ({
      ...previousInput,
      search_location: address || "",
      address: address || "",
      latitude: latitude || "",
      longitude: longitude || "",
    }));
  };

  /*
   * Load countries.
   */
  useEffect(() => {
    axios
      .get("/api/countryStateCity/list")
      .then((response) => {
        if (response.data?.success) {
          setCountries(
            response.data?.result?.items || []
          );
        }
      })
      .catch((error) => {
        console.error(
          "Country loading error:",
          error
        );
      });
  }, []);

  /*
   * Load post details and existing media.
   */
  const loadPostDetail = async () => {
    try {
      showLoader();

      const response = await axios.get(
        `/api/detail/post/site/${id}`,
        {
          headers: {
            Accept: "application/json",
          },
        }
      );

      const post =
        response.data?.result?.data ||
        response.data?.result ||
        response.data?.data;

      if (!post) {
        swal("Error", "Post not found", "error");
        return;
      }

      console.log("Edit post API result:", post);

      const countryId = post.country
        ? String(post.country)
        : "";

      const stateId = post.state
        ? String(post.state)
        : "";

      const cityId = post.city
        ? String(post.city)
        : "";

      prefillRef.current = {
        countryId,
        stateId,
        cityId,
      };

      setIsPrefill(true);

      setPostSiteInput((previousInput) => ({
        ...previousInput,
        user_id:
          post.user_id ||
          localStorage.getItem("user_id") ||
          "",
        item_type: post.item_type || "",
        project_name: post.project_name || "",
        cuyds_of_dirt_min:
          post.cuyds_of_dirt_min || "",
        cuyds_of_dirt_max:
          post.cuyds_of_dirt_max || "",
        search_location:
          post.search_location ||
          post.address ||
          "",
        address: post.address || "",
        latitude: post.latitude || "",
        longitude: post.longitude || "",
        description: post.description || "",
        special_instruction:
          post.special_instruction || "",
        name: post.name || "",
        email: post.email || "",
        phone: post.phone || "",
        company_name: post.company_name || "",
        country: countryId,
        state: stateId,
        city: cityId,
        status: post.status ?? 1,
      }));

      setSelectedCountryId(countryId);

      /*
       * Existing uploaded files.
       */
      const loadedImages = extractExistingMedia(
        post,
        "image"
      );

      const loadedVideos = extractExistingMedia(
        post,
        "video"
      );

      const loadedDocuments = extractExistingMedia(
        post,
        "document"
      );

      console.log(
        "Previously uploaded images:",
        loadedImages
      );

      console.log(
        "Previously uploaded videos:",
        loadedVideos
      );

      console.log(
        "Previously uploaded documents:",
        loadedDocuments
      );

      setExistingImages(loadedImages);
      setExistingVideos(loadedVideos);
      setExistingDocuments(loadedDocuments);

      setImages([]);
      setVideo([]);
      setDocumentFile([]);

      setRemovedImages([]);
      setRemovedVideos([]);
      setRemovedDocuments([]);
    } catch (error) {
      console.error(
        "Post detail loading error:",
        error
      );

      swal(
        "Error",
        error?.response?.data?.message ||
          "Failed to load post detail",
        "error"
      );
    } finally {
      hideLoader();
    }
  };

  useEffect(() => {
    loadPostDetail();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  /*
   * Load states when country changes.
   */
  useEffect(() => {
    const previousCountry =
      prevCountryIdRef.current;

    const countryChangedByUser =
      previousCountry !== "" &&
      previousCountry !== selectedCountryId;

    prevCountryIdRef.current =
      selectedCountryId;

    if (!selectedCountryId) {
      setStates([]);
      setCities([]);
      setSelectedStateId("");
      setSelectedCityId("");

      setPostSiteInput((previousInput) => ({
        ...previousInput,
        country: "",
        state: "",
        city: "",
      }));

      return;
    }

    axios
      .get(
        `/api/countryStateCity/list?country_id=${selectedCountryId}`
      )
      .then((response) => {
        const items =
          response.data?.result?.items || [];

        setStates(items);

        setPostSiteInput((previousInput) => ({
          ...previousInput,
          country: selectedCountryId,
        }));

        if (
          countryChangedByUser &&
          !isPrefill
        ) {
          setCities([]);
          setSelectedStateId("");
          setSelectedCityId("");

          setPostSiteInput((previousInput) => ({
            ...previousInput,
            state: "",
            city: "",
          }));

          return;
        }

        if (isPrefill) {
          const savedStateId =
            prefillRef.current.stateId;

          const stateExists = items.some(
            (state) =>
              String(state.id) ===
              String(savedStateId)
          );

          if (savedStateId && stateExists) {
            setSelectedStateId(
              String(savedStateId)
            );

            setPostSiteInput(
              (previousInput) => ({
                ...previousInput,
                state: String(savedStateId),
              })
            );
          } else {
            setSelectedStateId("");
            setSelectedCityId("");
          }
        }
      })
      .catch((error) => {
        console.error(
          "State loading error:",
          error
        );
      });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCountryId]);

  /*
   * Load cities when state changes.
   */
  useEffect(() => {
    const previousState =
      prevStateIdRef.current;

    const stateChangedByUser =
      previousState !== "" &&
      previousState !== selectedStateId;

    prevStateIdRef.current = selectedStateId;

    if (!selectedStateId) {
      setCities([]);
      setSelectedCityId("");

      setPostSiteInput((previousInput) => ({
        ...previousInput,
        state: "",
        city: "",
      }));

      return;
    }

    axios
      .get(
        `/api/countryStateCity/list?state_id=${selectedStateId}`
      )
      .then((response) => {
        const items =
          response.data?.result?.items || [];

        setCities(items);

        setPostSiteInput((previousInput) => ({
          ...previousInput,
          state: selectedStateId,
        }));

        if (
          stateChangedByUser &&
          !isPrefill
        ) {
          setSelectedCityId("");

          setPostSiteInput((previousInput) => ({
            ...previousInput,
            city: "",
          }));

          return;
        }

        if (isPrefill) {
          const savedCityId =
            prefillRef.current.cityId;

          const cityExists = items.some(
            (city) =>
              String(city.id) ===
              String(savedCityId)
          );

          if (savedCityId && cityExists) {
            setSelectedCityId(
              String(savedCityId)
            );

            setPostSiteInput(
              (previousInput) => ({
                ...previousInput,
                city: String(savedCityId),
              })
            );
          } else {
            setSelectedCityId("");
          }

          setIsPrefill(false);
        }
      })
      .catch((error) => {
        console.error(
          "City loading error:",
          error
        );
      });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStateId]);

  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      swal(
        "Error",
        "Geolocation is not supported by your browser",
        "error"
      );

      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const latitude =
          position.coords.latitude;

        const longitude =
          position.coords.longitude;

        if (
          !window.google ||
          !window.google.maps
        ) {
          setPostSiteInput(
            (previousInput) => ({
              ...previousInput,
              latitude,
              longitude,
            })
          );

          swal(
            "Warning",
            "Google Maps is not loaded. Coordinates were updated without an address.",
            "warning"
          );

          return;
        }

        const geocoder =
          new window.google.maps.Geocoder();

        geocoder.geocode(
          {
            location: {
              lat: latitude,
              lng: longitude,
            },
          },
          (results, status) => {
            if (
              status === "OK" &&
              results?.[0]
            ) {
              const address =
                results[0].formatted_address;

              setPostSiteInput(
                (previousInput) => ({
                  ...previousInput,
                  search_location: address,
                  address,
                  latitude,
                  longitude,
                })
              );

              return;
            }

            setPostSiteInput(
              (previousInput) => ({
                ...previousInput,
                latitude,
                longitude,
              })
            );

            console.error(
              "Geocoder failed:",
              status
            );
          }
        );
      },
      (error) => {
        console.error(
          "Geolocation error:",
          error
        );

        swal(
          "Error",
          "Unable to retrieve your location",
          "error"
        );
      },
      {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 60000,
      }
    );
  };

  /*
   * Submit updated post.
   */
  const submitPostSite = async (event) => {
    event.preventDefault();

    try {
      showLoader();
      setError({});

      const userId =
        localStorage.getItem("user_id");

      if (!userId) {
        swal(
          "Error",
          "User not logged in",
          "error"
        );

        return;
      }

      const formData = new FormData();

      formData.append("user_id", userId);

      formData.append(
        "item_type",
        postSiteInput.item_type || ""
      );

      formData.append(
        "project_name",
        postSiteInput.project_name || ""
      );

      formData.append(
        "cuyds_of_dirt_min",
        postSiteInput.cuyds_of_dirt_min || ""
      );

      formData.append(
        "cuyds_of_dirt_max",
        postSiteInput.cuyds_of_dirt_max || ""
      );

      formData.append(
        "address",
        postSiteInput.address || ""
      );

      formData.append(
        "latitude",
        postSiteInput.latitude || ""
      );

      formData.append(
        "longitude",
        postSiteInput.longitude || ""
      );

      formData.append(
        "description",
        postSiteInput.description || ""
      );

      formData.append(
        "special_instruction",
        postSiteInput.special_instruction || ""
      );

      formData.append(
        "name",
        postSiteInput.name || ""
      );

      formData.append(
        "email",
        postSiteInput.email || ""
      );

      formData.append(
        "phone",
        postSiteInput.phone || ""
      );

      formData.append(
        "company_name",
        postSiteInput.company_name || ""
      );

      formData.append(
        "country",
        postSiteInput.country || ""
      );

      formData.append(
        "state",
        postSiteInput.state || ""
      );

      formData.append(
        "city",
        postSiteInput.city || ""
      );

      formData.append(
        "status",
        postSiteInput.status ?? 1
      );

      /*
       * Existing files still visible in the edit form.
       */
      formData.append(
        "existing_images",
        JSON.stringify(
          existingImages.map((file) => ({
            id: file.id,
            path: file.path,
          }))
        )
      );

      formData.append(
        "existing_videos",
        JSON.stringify(
          existingVideos.map((file) => ({
            id: file.id,
            path: file.path,
          }))
        )
      );

      formData.append(
        "existing_documents",
        JSON.stringify(
          existingDocuments.map((file) => ({
            id: file.id,
            path: file.path,
          }))
        )
      );

      /*
       * Existing files removed by the user.
       */
      formData.append(
        "removed_images",
        JSON.stringify(removedImages)
      );

      formData.append(
        "removed_videos",
        JSON.stringify(removedVideos)
      );

      formData.append(
        "removed_documents",
        JSON.stringify(removedDocuments)
      );

      /*
       * Newly selected files.
       */
      images.forEach((file) => {
        formData.append("image[]", file);
      });

      video.forEach((file) => {
        formData.append("video[]", file);
      });

      documentFile.forEach((file) => {
        formData.append("document[]", file);
      });

      const response = await axios.post(
        `/api/update/post/site/${id}/${userId}`,
        formData,
        {
          headers: {
            Accept: "application/json",
          },
        }
      );

      if (
        response.data?.status_code === 422
      ) {
        const validationErrors =
          response.data?.errors || {};

        setError(validationErrors);

        const firstErrorKey =
          Object.keys(validationErrors)[0];

        const firstErrorMessage =
          firstErrorKey
            ? validationErrors[firstErrorKey]?.[0]
            : "Validation error";

        swal(
          "Error",
          firstErrorMessage,
          "error"
        );

        return;
      }

      if (
        response.data?.status_code === 200 ||
        response.data?.success === true
      ) {
        await swal(
          "Success",
          response.data?.message ||
            "Post updated successfully",
          "success"
        );

        history.push("/my-posts");
        return;
      }

      swal(
        "Error",
        response.data?.message ||
          "Update failed",
        "error"
      );
    } catch (error) {
      console.error(
        "Post update error:",
        error
      );

      if (error?.response?.status === 413) {
        await swal({
          title: "Files Too Large",
          text:
            "The total upload size is too large. Please upload smaller files or fewer files.",
          icon: "error",
          button: "OK",
        });

        return;
      }

      const validationErrors =
        error?.response?.data?.errors;

      if (validationErrors) {
        setError(validationErrors);

        const firstErrorKey =
          Object.keys(validationErrors)[0];

        const firstErrorMessage =
          firstErrorKey
            ? validationErrors[firstErrorKey]?.[0]
            : "Validation error";

        swal(
          "Error",
          firstErrorMessage,
          "error"
        );

        return;
      }

      swal(
        "Error",
          error?.response?.data?.message ||
          error?.message ||
          "Update failed",
        "error"
      );
    } finally {
      hideLoader();
    }
  };

  return (
    <>
      <div
        id="demo"
        className="contactForm"
      >
        <div className="shadowBox m-30">
          <form onSubmit={submitPostSite}>
            <input
              type="hidden"
              name="user_id"
              value={postSiteInput.user_id}
              readOnly
            />

            <input
              type="hidden"
              id="__postSearchLocation"
              name="address"
              value={
                postSiteInput.search_location
              }
              readOnly
            />

            <input
              type="hidden"
              id="__postLatitude"
              name="latitude"
              value={postSiteInput.latitude}
              readOnly
            />

            <input
              type="hidden"
              id="__postLongitude"
              name="longitude"
              value={postSiteInput.longitude}
              readOnly
            />

            <div className="postItemsForm lightPinkBg02">
              <h2 className="mt-3 mb-3">
                Edit Your Site
              </h2>

              <div className="customFormRow customFormRow02">
                <div className="labelBox">
                  Select Item Type
                </div>

                <div className="inputField">
                  <select
                    name="item_type"
                    ref={postFormRef}
                    onChange={handleInput}
                    value={
                      postSiteInput.item_type
                    }
                    className="form-control"
                  >
                    <option value="">
                      Type
                    </option>

                    <option value="import">
                      Soil Import
                    </option>

                    <option value="export">
                      Soil Export
                    </option>

                    <option value="dump_site">
                      Dump Site
                    </option>
                  </select>

                  <span className="validate-error-message">
                    {
                      errorlist.item_type?.[0]
                    }
                  </span>
                </div>
              </div>

              <div className="customFormRow customFormRow02">
                <div className="labelBox">
                  Project Name
                </div>

                <div className="inputField">
                  <input
                    type="text"
                    name="project_name"
                    onChange={handleInput}
                    value={
                      postSiteInput.project_name
                    }
                    className="form-control"
                    placeholder="Enter Project Name"
                  />

                  <span className="validate-error-message">
                    {
                      errorlist.project_name?.[0]
                    }
                  </span>
                </div>
              </div>

              <div className="customFormRow customFormRow02">
                <div className="labelBox">
                  CuYds of Dirt Min
                </div>

                <div className="inputField">
                  <input
                    type="text"
                    name="cuyds_of_dirt_min"
                    onChange={handleInput}
                    value={
                      postSiteInput.cuyds_of_dirt_min
                    }
                    className="form-control"
                    placeholder="Enter CuYds Of Dirt"
                  />

                  <span className="validate-error-message">
                    {
                      errorlist
                        .cuyds_of_dirt_min?.[0]
                    }
                  </span>
                </div>
              </div>

              <div className="customFormRow customFormRow02">
                <div className="labelBox">
                  CuYds of Dirt Max
                </div>

                <div className="inputField">
                  <input
                    type="text"
                    name="cuyds_of_dirt_max"
                    onChange={handleInput}
                    value={
                      postSiteInput.cuyds_of_dirt_max
                    }
                    className="form-control"
                    placeholder="Enter CuYds Of Dirt"
                  />

                  <span className="validate-error-message">
                    {
                      errorlist
                        .cuyds_of_dirt_max?.[0]
                    }
                  </span>
                </div>
              </div>

              <div className="customFormRow customFormRow02">
                <div className="labelBox">
                  Add Location
                </div>

                <div className="inputField">
                  {isDisplayed && (
                    <PostAutoComplete
                      setPostCoodinationValue={
                        setPostCoodinationValue
                      }
                      address={
                        postSiteInput.address
                      }
                    />
                  )}

                  <button
                    type="button"
                    className="btn btn-sm btn-primary mt-2"
                    onClick={getCurrentLocation}
                  >
                    Use Current Location
                  </button>

                  <span className="validate-error-message">
                    {errorlist.address?.[0]}
                  </span>
                </div>

                <div className="fieldsetInfoText">
                  Add location on Google Map
                  or use your current location
                </div>
              </div>

              <div className="customFormRow message customFormRow02">
                <div className="labelBox">
                  Description
                </div>

                <div className="inputField">
                  <textarea
                    name="description"
                    onChange={handleInput}
                    value={
                      postSiteInput.description
                    }
                    className="form-control"
                    placeholder="Enter Description"
                  />

                  <span className="validate-error-message">
                    {
                      errorlist.description?.[0]
                    }
                  </span>
                </div>
              </div>
            </div>

            <div className="postItemsForm lightPinkBg02">
              <h6 className="mb-2">
                Upload Images, Videos,
                Documents etc.
              </h6>

              {/* Images */}
              <div className="customFormRow customFormRow02">
                <div className="labelBox">
                  Images
                </div>

                <div className="inputField">
                  <input
                    type="file"
                    name="image[]"
                    onChange={handleImage}
                    multiple
                    accept="image/*"
                    className="form-control"
                  />

                  <span className="validate-error-message">
                    {errorlist["image.0"]?.[0] ||
                      errorlist.image?.[0]}
                  </span>

                  {existingImages.length >
                    0 && (
                    <>
                      <h6
                        style={{
                          marginTop: 15,
                        }}
                      >
                        Previously uploaded
                        images
                      </h6>

                      <div
                        className="preview-container"
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 12,
                        }}
                      >
                        {existingImages.map(
                          (file, index) => (
                            <div
                              key={`existing-image-${
                                file.id ||
                                file.path ||
                                index
                              }`}
                              className="preview-box"
                            >
                              <a
                                href={file.url}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <img
                                  src={file.url}
                                  width="120"
                                  height="100"
                                  alt={
                                    file.name
                                  }
                                  style={{
                                    objectFit:
                                      "cover",
                                    borderRadius:
                                      4,
                                  }}
                                />
                              </a>

                              <p
                                title={file.name}
                                style={{
                                  fontSize: 11,
                                  maxWidth: 120,
                                  overflow:
                                    "hidden",
                                  textOverflow:
                                    "ellipsis",
                                  whiteSpace:
                                    "nowrap",
                                  marginBottom: 5,
                                }}
                              >
                                {file.name}
                              </p>

                              <button
                                type="button"
                                onClick={() =>
                                  removeExistingImage(
                                    file
                                  )
                                }
                              >
                                ❌
                              </button>
                            </div>
                          )
                        )}
                      </div>
                    </>
                  )}

                  {images.length > 0 && (
                    <>
                      <h6
                        style={{
                          marginTop: 15,
                        }}
                      >
                        Newly selected images
                      </h6>

                      <div
                        className="preview-container"
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 12,
                        }}
                      >
                        {images.map(
                          (file, index) => (
                            <div
                              key={`new-image-${file.name}-${index}`}
                              className="preview-box"
                            >
                              <img
                                src={URL.createObjectURL(
                                  file
                                )}
                                width="120"
                                height="100"
                                alt={file.name}
                                style={{
                                  objectFit:
                                    "cover",
                                  borderRadius: 4,
                                }}
                              />

                              <p
                                title={file.name}
                                style={{
                                  fontSize: 11,
                                  maxWidth: 120,
                                  overflow:
                                    "hidden",
                                  textOverflow:
                                    "ellipsis",
                                  whiteSpace:
                                    "nowrap",
                                  marginBottom: 5,
                                }}
                              >
                                {file.name}
                              </p>

                              <button
                                type="button"
                                onClick={() =>
                                  removeImage(
                                    index
                                  )
                                }
                              >
                                ❌
                              </button>
                            </div>
                          )
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Videos */}
              <div className="customFormRow customFormRow02">
                <div className="labelBox">
                  Video
                </div>

                <div className="inputField">
                  <input
                    type="file"
                    name="video[]"
                    onChange={handleVideo}
                    multiple
                    accept="video/mp4,video/quicktime,video/ogg,video/webm"
                    className="form-control"
                  />

                  <span className="validate-error-message">
                    {errorlist["video.0"]?.[0] ||
                      errorlist.video?.[0]}
                  </span>

                  {existingVideos.length >
                    0 && (
                    <>
                      <h6
                        style={{
                          marginTop: 15,
                        }}
                      >
                        Previously uploaded
                        videos
                      </h6>

                      <div
                        className="preview-container"
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 12,
                        }}
                      >
                        {existingVideos.map(
                          (file, index) => (
                            <div
                              key={`existing-video-${
                                file.id ||
                                file.path ||
                                index
                              }`}
                              className="preview-box"
                            >
                              <video
                                width="180"
                                height="120"
                                controls
                                preload="metadata"
                              >
                                <source
                                  src={file.url}
                                />

                                Your browser does
                                not support video.
                              </video>

                              <p
                                title={file.name}
                                style={{
                                  fontSize: 11,
                                  maxWidth: 180,
                                  overflow:
                                    "hidden",
                                  textOverflow:
                                    "ellipsis",
                                  whiteSpace:
                                    "nowrap",
                                  marginBottom: 5,
                                }}
                              >
                                {file.name}
                              </p>

                              <a
                                href={file.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  marginRight:
                                    10,
                                }}
                              >
                                Open
                              </a>

                              <button
                                type="button"
                                onClick={() =>
                                  removeExistingVideo(
                                    file
                                  )
                                }
                              >
                                ❌
                              </button>
                            </div>
                          )
                        )}
                      </div>
                    </>
                  )}

                  {video.length > 0 && (
                    <>
                      <h6
                        style={{
                          marginTop: 15,
                        }}
                      >
                        Newly selected videos
                      </h6>

                      <div
                        className="preview-container"
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 12,
                        }}
                      >
                        {video.map(
                          (file, index) => (
                            <div
                              key={`new-video-${file.name}-${index}`}
                              className="preview-box"
                            >
                              <video
                                width="180"
                                height="120"
                                controls
                              >
                                <source
                                  src={URL.createObjectURL(
                                    file
                                  )}
                                  type={
                                    file.type
                                  }
                                />
                              </video>

                              <p
                                title={file.name}
                                style={{
                                  fontSize: 11,
                                  maxWidth: 180,
                                  overflow:
                                    "hidden",
                                  textOverflow:
                                    "ellipsis",
                                  whiteSpace:
                                    "nowrap",
                                  marginBottom: 5,
                                }}
                              >
                                {file.name}
                              </p>

                              <button
                                type="button"
                                onClick={() =>
                                  removeVideo(
                                    index
                                  )
                                }
                              >
                                ❌
                              </button>
                            </div>
                          )
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Documents */}
              <div className="customFormRow customFormRow02">
                <div className="labelBox">
                  Document
                </div>

                <div className="inputField">
                  <input
                    type="file"
                    name="document[]"
                    multiple
                    onChange={handleDocument}
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.ppt,.pptx"
                    className="form-control"
                  />

                  <span className="validate-error-message">
                    {
                      errorlist[
                        "document.0"
                      ]?.[0]
                    }

                    {!errorlist[
                      "document.0"
                    ]?.[0] &&
                      errorlist.document?.[0]}
                  </span>

                  {existingDocuments.length >
                    0 && (
                    <>
                      <h6
                        style={{
                          marginTop: 15,
                        }}
                      >
                        Previously uploaded
                        documents
                      </h6>

                      <div className="preview-container">
                        {existingDocuments.map(
                          (file, index) => (
                            <div
                              key={`existing-document-${
                                file.id ||
                                file.path ||
                                index
                              }`}
                              className="preview-box"
                              style={{
                                display: "flex",
                                alignItems:
                                  "center",
                                gap: 10,
                                marginBottom:
                                  10,
                              }}
                            >
                              <i
                                className="fa fa-file"
                                aria-hidden="true"
                              />

                              <a
                                href={file.url}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {file.name}
                              </a>

                              <button
                                type="button"
                                onClick={() =>
                                  removeExistingDocument(
                                    file
                                  )
                                }
                              >
                                ❌
                              </button>
                            </div>
                          )
                        )}
                      </div>
                    </>
                  )}

                  {documentFile.length >
                    0 && (
                    <>
                      <h6
                        style={{
                          marginTop: 15,
                        }}
                      >
                        Newly selected documents
                      </h6>

                      <div className="preview-container">
                        {documentFile.map(
                          (file, index) => (
                            <div
                              key={`new-document-${file.name}-${index}`}
                              className="preview-box"
                              style={{
                                display: "flex",
                                alignItems:
                                  "center",
                                gap: 10,
                                marginBottom:
                                  10,
                              }}
                            >
                              <i
                                className="fa fa-file"
                                aria-hidden="true"
                              />

                              <p
                                style={{
                                  fontSize: 12,
                                  margin: 0,
                                }}
                              >
                                {file.name}
                              </p>

                              <button
                                type="button"
                                onClick={() =>
                                  removeDocument(
                                    index
                                  )
                                }
                              >
                                ❌
                              </button>
                            </div>
                          )
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>

              <div className="customFormRow message customFormRow02">
                <div className="labelBox">
                  Special Instructions
                </div>

                <div className="inputField">
                  <textarea
                    name="special_instruction"
                    onChange={handleInput}
                    value={
                      postSiteInput.special_instruction
                    }
                    className="form-control"
                    placeholder="Enter Special Instruction"
                  />

                  <span className="validate-error-message">
                    {
                      errorlist
                        .special_instruction?.[0]
                    }
                  </span>
                </div>
              </div>
            </div>

            <div className="postItemsForm lightPinkBg02">
              <h6 className="mb-2">
                Contact Info
              </h6>

              <div className="customFormRow customFormRow02">
                <div className="labelBox">
                  Name
                </div>

                <div className="inputField">
                  <input
                    type="text"
                    name="name"
                    onChange={handleInput}
                    value={postSiteInput.name}
                    className="form-control"
                    placeholder="Enter Name"
                  />

                  <span className="validate-error-message">
                    {errorlist.name?.[0]}
                  </span>
                </div>
              </div>

              <div className="customFormRow customFormRow02">
                <div className="labelBox">
                  Email
                </div>

                <div className="inputField">
                  <input
                    type="text"
                    name="email"
                    onChange={handleInput}
                    value={postSiteInput.email}
                    className="form-control"
                    placeholder="Enter Email"
                  />

                  <span className="validate-error-message">
                    {errorlist.email?.[0]}
                  </span>
                </div>
              </div>

              <div className="customFormRow customFormRow02">
                <div className="labelBox">
                  Phone Number
                </div>

                <div className="inputField">
                  <input
                    type="text"
                    name="phone"
                    onChange={handleInput}
                    value={postSiteInput.phone}
                    className="form-control"
                    placeholder="Enter Phone Number"
                  />

                  <span className="validate-error-message">
                    {errorlist.phone?.[0]}
                  </span>
                </div>
              </div>

              <div className="customFormRow customFormRow02">
                <div className="labelBox">
                  Company Name
                </div>

                <div className="inputField">
                  <input
                    type="text"
                    name="company_name"
                    onChange={handleInput}
                    value={
                      postSiteInput.company_name
                    }
                    className="form-control"
                    placeholder="Enter Company Name"
                  />

                  <span className="validate-error-message">
                    {
                      errorlist.company_name?.[0]
                    }
                  </span>
                </div>
              </div>

              <div className="customFormRow customFormRow02">
                <div className="labelBox">
                  Country
                </div>

                <div className="inputField">
                  <select
                    className="form-control"
                    value={selectedCountryId}
                    onChange={(event) => {
                      const countryId =
                        event.target.value;

                      setSelectedCountryId(
                        countryId
                      );

                      setPostSiteInput(
                        (previousInput) => ({
                          ...previousInput,
                          country: countryId,
                        })
                      );
                    }}
                  >
                    <option value="">
                      Select Country
                    </option>

                    {countries.map(
                      (country) => (
                        <option
                          key={country.id}
                          value={String(
                            country.id
                          )}
                        >
                          {country.name}
                        </option>
                      )
                    )}
                  </select>

                  <span className="validate-error-message">
                    {
                      errorlist.country?.[0]
                    }
                  </span>
                </div>
              </div>

              <div className="customFormRow customFormRow02">
                <div className="labelBox">
                  State
                </div>

                <div className="inputField">
                  <select
                    className="form-control"
                    value={selectedStateId}
                    disabled={
                      !selectedCountryId
                    }
                    onChange={(event) => {
                      const stateId =
                        event.target.value;

                      setSelectedStateId(
                        stateId
                      );

                      setPostSiteInput(
                        (previousInput) => ({
                          ...previousInput,
                          state: stateId,
                        })
                      );
                    }}
                  >
                    <option value="">
                      Select State
                    </option>

                    {states.map((state) => (
                      <option
                        key={state.id}
                        value={String(
                          state.id
                        )}
                      >
                        {state.name}
                      </option>
                    ))}
                  </select>

                  <span className="validate-error-message">
                    {errorlist.state?.[0]}
                  </span>
                </div>
              </div>

              <div className="customFormRow customFormRow02">
                <div className="labelBox">
                  City
                </div>

                <div className="inputField">
                  <select
                    className="form-control"
                    value={selectedCityId}
                    disabled={!selectedStateId}
                    onChange={(event) => {
                      const cityId =
                        event.target.value;

                      setSelectedCityId(cityId);

                      setPostSiteInput(
                        (previousInput) => ({
                          ...previousInput,
                          city: cityId,
                        })
                      );
                    }}
                  >
                    <option value="">
                      Select City
                    </option>

                    {cities.map((city) => (
                      <option
                        key={city.id}
                        value={String(city.id)}
                      >
                        {city.name}
                      </option>
                    ))}
                  </select>

                  <span className="validate-error-message">
                    {errorlist.city?.[0]}
                  </span>
                </div>
              </div>
            </div>

            <div className="postItemsForm">
              <div className="customFormRow last">
                <div className="inputField">
                  <input
                    type="submit"
                    value="Update Post"
                    className="button customButton01 post-submit active submitButton"
                  />
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>

      {loader}
    </>
  );
};

export default EditPostSite;